import { Icon } from '@iconify/react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import MuiLink from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { logError } from '../../../utils/logger';
import { useAuth } from '../auth.context';
import { StyledInput } from './StyledInput';

export default function LoginForm() {
  const { login, loading } = useAuth();
  const navigate = useNavigate();

  const [showPassword, setShowPassword] = useState(false);

  const methods = useForm({
    defaultValues: { username: '', password: '' },
    mode: 'onChange',
  });
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = methods;

  const togglePasswordVisibility = () => setShowPassword((prev) => !prev);

  const onSubmit = async (data) => {
    try {
      const { success, error } = await login(data);
      if (success) navigate('/');
      else throw new Error(error);
    } catch (err) {
      toast.error(
        logError({ message: 'Authentication failed', error: err, code: 'AUTH_ACTION_FAILED' })
      );
    }
  };

  return (
    <FormProvider {...methods}>
      <Stack
        component='form'
        sx={{ width: '100%', maxWidth: '400px', gap: 8 }}
        onSubmit={handleSubmit(onSubmit)}
      >
        <Typography
          variant='h1'
          fontSize='2rem'
          fontWeight={600}
          textAlign='center'
          color='primary.contrastText'
        >
          LOGIN
        </Typography>

        <Stack gap={5}>
          <Stack gap={1}>
            <Typography
              component='label'
              htmlFor='username'
              fontSize='1.25rem'
              color='primary.contrastText'
            >
              Username
            </Typography>
            <Controller
              name='username'
              control={control}
              rules={{ required: 'Username is required.' }}
              render={({ field }) => (
                <StyledInput {...field} id='username' type='text' autoComplete='username' />
              )}
            />
            {errors.username && (
              <Typography color='error' fontSize='0.75rem'>
                {errors.username.message}
              </Typography>
            )}
          </Stack>

          <Stack gap={1}>
            <Typography
              component='label'
              htmlFor='password'
              fontSize='1.25rem'
              color='primary.contrastText'
            >
              Password
            </Typography>
            <Box position='relative' width='100%'>
              <Controller
                name='password'
                control={control}
                rules={{
                  required: 'Password is required.',
                  minLength: { value: 6, message: 'Password must be at least 6 characters.' },
                }}
                render={({ field }) => (
                  <StyledInput
                    {...field}
                    id='password'
                    type={showPassword ? 'text' : 'password'}
                    autoComplete='current-password'
                  />
                )}
              />
              <IconButton
                type='button'
                onClick={togglePasswordVisibility}
                aria-label='Toggle password visibility'
                sx={{
                  position: 'absolute',
                  right: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'primary.contrastText',
                }}
              >
                <Icon icon={showPassword ? 'mdi:eye-off' : 'mdi:eye'} />
              </IconButton>
            </Box>
            {errors.password && (
              <Typography color='error' fontSize='0.75rem'>
                {errors.password.message}
              </Typography>
            )}
            <MuiLink component={Link} to='/reset-password' sx={{ width: 'fit-content' }}>
              Reset Password
            </MuiLink>
          </Stack>

          <Button
            type='submit'
            variant='contained'
            loading={loading}
            sx={{
              width: '100%',
              padding: '12px 32px',
              fontSize: '1.25rem',
              borderRadius: 2,
              backgroundColor: 'primary.contrastText',
              color: 'primary.main',
              ':disabled': {
                backgroundColor: 'primary.contrastText',
                color: 'primary.main',
                opacity: 0.5,
              },
            }}
          >
            {loading ? 'Logging in…' : 'Login'}
          </Button>
        </Stack>
      </Stack>
    </FormProvider>
  );
}
